import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../firebaseConfig';
import {
  createUserWithEmailAndPassword,
  onAuthStateChanged,
} from 'firebase/auth';
import { 
  collection, doc, getDoc, getDocs, query, serverTimestamp, setDoc, where,
  runTransaction, arrayUnion } from 'firebase/firestore';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import PhoneVerify from './VerifyPhone';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { STATES } from '../utils/states';
import RegistrationProgress from '../components/RegistrationProgress';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBriefcase,
  faBuilding,
  faChevronRight,
  faEnvelope,
  faHeadset,
  faLayerGroup,
  faLocationDot,
  faLock,
  faShieldHalved,
  faUser,
  faUserShield,
  faPhone,
} from '@fortawesome/free-solid-svg-icons';



	// ---- Rank abbreviation helpers ----
const RANK_ABBREV = {
  // Common patrol ranks/titles
  'Officer': 'Ofc.',
  'Senior Officer': 'S.O.',
  'Master Officer': 'M.O.',
  'Field Training Officer': 'F.T.O.',
  'Corporal': 'Cpl.',
  'Sergeant': 'Sgt.',
  'Lieutenant': 'Lt.',
  'Captain': 'Capt.',
  'Major': 'Major',
  'Chief': 'Chief',
  'Deputy Sheriff': 'Dep.',
  'Sheriff': 'Sheriff',
  'Detective': 'Det.',
  'Investigator': 'Inv.',
  'Special Agent': 'S.A.',
};

const normalizeAgencyName = (value) =>
  String(value || '')
    .replace(/[\u2018\u2019\u201A\u201B]/g, "'")
    .replace(/[\u201C\u201D\u201E\u201F]/g, '"')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

const sanitizeAgencyName = (value) =>
  String(value || '')
    .replace(/\s+/g, ' ')
    .trim();

const buildAgencyLookup = (agencies = []) => {
  const rawAgencies = [...new Set(agencies.filter(Boolean))]
    .sort((a, b) => a.localeCompare(b));
  const sortedAgencies = [];
  const normalizedMap = new Map();
  const searchEntries = [];

  rawAgencies.forEach((agencyName) => {
    const normalized = normalizeAgencyName(agencyName);
    if (!normalized || normalizedMap.has(normalized)) return;
    normalizedMap.set(normalized, agencyName);
    sortedAgencies.push(agencyName);
    searchEntries.push({ normalized, agencyName });
  });

  return { sortedAgencies, normalizedMap, searchEntries };
};

function InfoRow({ icon, children }) {
  return (
    <div className="flex gap-4 border-b border-slate-100 p-4 last:border-b-0">
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
        {icon}
      </div>
      <p className="self-center text-sm leading-6 text-slate-800 sm:text-base">
        {children}
      </p>
    </div>
  );
}

function FieldShell({ icon, children }) {
  return (
    <div className="flex gap-3 sm:gap-4">
      <div className="mt-7 flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-blue-100 bg-blue-50 text-blue-600 shadow-sm">
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        {children}
      </div>
    </div>
  );
}


export default function Register() {
  const navigate = useNavigate();
  const textInputClass = 'input block w-full min-h-[44px] border border-gray-300/80 bg-white shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-100';
  const selectInputClass = 'input block w-full min-h-[44px] border border-gray-300/80 bg-gray-50 shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-100';
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [middleInitial, setMiddleInitial] = useState('');
  const [lastName, setLastName] = useState('');
  const [username, setUsername] = useState('');
  const [agency, setAgency] = useState('');
  const [department, setDepartment] = useState('');
  const [rank, setRank] = useState('');
  const [state, setState] = useState('');
  const [isFederal, setIsFederal] = useState(false);
  const [referral, setReferral] = useState('');
  const [referrerFirst, setReferrerFirst] = useState('');
  const [referrerLast, setReferrerLast] = useState('');
  const [referrerAgency, setReferrerAgency] = useState('');
  const [agencyOptions, setAgencyOptions] = useState([]);
  const [agencyLookup, setAgencyLookup] = useState(() => new Map());
  const [agencySearchEntries, setAgencySearchEntries] = useState([]);
  const [rankOptions, setRankOptions] = useState([]);
  const [assignmentOptions, setAssignmentOptions] = useState([]);
  const [customAgency, setCustomAgency] = useState('');
  const [customRank, setCustomRank] = useState('');
  const [customAssignment, setCustomAssignment] = useState('');
  const [referrerNumber, setReferrerNumber] = useState('');
  const [showReferralModal, setShowReferralModal] = useState(false);
  const [referralCheckPassed, setReferralCheckPassed] = useState(false);
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [isResettingRegistration, setIsResettingRegistration] = useState(false);

  const handlePhoneInputChange = (e) => {
    const digitsOnly = String(e.target.value || '').replace(/\D/g, '').slice(0, 11);
    setPhone(digitsOnly);
  };

  

const blocklist = [
  'gmail.com',
  'yahoo.com',
  'hotmail.com',
  'outlook.com',
  'live.com',
  'aol.com',
  'icloud.com',
  'msn.com',
  'me.com',
  'proton.me',
  'protonmail.com',
  'protonmail.ch',
  'pm.me',
  'gmx.com',
  'yandex.com',
  'mail.com',
  'zoho.com',
  'fastmail.com',
  'hey.com',
  'qq.com',
  'att.net',
  'comcast.net',
  'verizon.net',
];



	const functions = getFunctions();
const emailVerificationViaResend = httpsCallable(
  functions,
  'emailVerificationViaResend'
);
const cleanupIncompleteRegistration = httpsCallable(
  functions,
  'cleanupIncompleteRegistration'
);


	// ---- Upsert helpers for config/registrationOptions ----
async function upsertConfigArray(db, field, item) {
  const val = (item || '').trim();
  if (!val) return;

  const ref = doc(db, 'config', 'registrationOptions');

  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    const data = snap.exists() ? snap.data() : {};
    const current = Array.isArray(data[field]) ? data[field] : [];

    // case-insensitive duplicate check
    const exists = current.some(v => (v || '').trim().toLowerCase() === val.toLowerCase());
    if (exists) return;

    const updated = [...current, val].sort((a, b) => a.localeCompare(b));

    if (snap.exists()) {
      tx.update(ref, { [field]: updated });
    } else {
      tx.set(ref, { [field]: updated });
    }
  });
}



function getNextRenewalDate() {
  const today = new Date();

  const nextMonthDate = new Date(today);
  nextMonthDate.setMonth(nextMonthDate.getMonth() + 1);

  const year = nextMonthDate.getFullYear();
  const monthStr = (nextMonthDate.getMonth() + 1).toString().padStart(2, '0');
  const dayStr = nextMonthDate.getDate().toString().padStart(2, '0');

  return `${year}-${monthStr}-${dayStr}`;
}


	useEffect(() => {
  let cancelled = false;

  const loadRegistrationOptions = async () => {
    try {
      const snap = await getDoc(doc(db, 'config', 'registrationOptions'));
      if (!snap.exists()) {
        console.warn('⚠️ config/registrationOptions is missing');
        return;
      }
      const d = snap.data();
      if (cancelled) return;

      setRankOptions(Array.isArray(d.ranks) ? [...d.ranks].sort() : []);
      setAssignmentOptions(Array.isArray(d.assignments) ? [...d.assignments].sort() : []);
    } catch (e) {
      console.error('Failed to load registration options:', e);
    }
  };

  loadRegistrationOptions();
  return () => { cancelled = true; };
}, []);

  useEffect(() => {
    let cancelled = false;

    const bootstrapStateAgenciesFromUsers = async (stateName) => {
      const usersQuery = query(
        collection(db, 'users'),
        where('state', '==', stateName)
      );
      const usersSnapshot = await getDocs(usersQuery);
      const userAgencies = [];

      usersSnapshot.forEach((userDoc) => {
        const agencyName = sanitizeAgencyName(userDoc.data()?.agency || '');
        if (agencyName) {
          userAgencies.push(agencyName);
        }
      });

      const { sortedAgencies } = buildAgencyLookup(userAgencies);

      if (sortedAgencies.length > 0) {
        await setDoc(
          doc(db, 'agenciesByState', stateName),
          { agencies: sortedAgencies },
          { merge: true }
        );
      }

      return sortedAgencies;
    };

    const loadStateAgencies = async () => {
      if (!state) {
        setAgencyOptions([]);
        setAgencyLookup(new Map());
        setAgencySearchEntries([]);
        return;
      }

      try {
        const stateRef = doc(db, 'agenciesByState', state);
        const stateSnap = await getDoc(stateRef);
        let agencies = stateSnap.exists() && Array.isArray(stateSnap.data().agencies)
          ? stateSnap.data().agencies
          : [];

        if (agencies.length === 0) {
          agencies = await bootstrapStateAgenciesFromUsers(state);
        }

        const { sortedAgencies, normalizedMap, searchEntries } = buildAgencyLookup(agencies);

        if (cancelled) return;
        setAgencyOptions(sortedAgencies);
        setAgencyLookup(normalizedMap);
        setAgencySearchEntries(searchEntries);
      } catch (e) {
        console.error('Failed to load state agencies:', e);
        if (cancelled) return;
        setAgencyOptions([]);
        setAgencyLookup(new Map());
        setAgencySearchEntries([]);
      }
    };

    loadStateAgencies();
    return () => { cancelled = true; };
  }, [state]);

  const handleStateChange = (e) => {
    const nextState = e.target.value;
    setState(nextState);
    setAgency('');
    setCustomAgency('');
  };

  const handleAgencyChange = (e) => {
    const nextAgency = e.target.value;
    setAgency(nextAgency);
    if (nextAgency !== 'Other') {
      setCustomAgency('');
    }
  };

  const handleAgencyDropdownBlocked = (e) => {
    if (state) return;
    e.preventDefault();
    toast.info('Select your home state first.');
  };

  const customAgencyInput = sanitizeAgencyName(customAgency);
  const normalizedCustomAgencyInput = normalizeAgencyName(customAgencyInput);
  const customAgencySuggestions =
    agency === 'Other' && normalizedCustomAgencyInput
      ? agencySearchEntries
          .filter(({ normalized }) => normalized.startsWith(normalizedCustomAgencyInput))
          .map(({ agencyName }) => agencyName)
          .slice(0, 8)
      : [];

  const resolveAgencyName = () => {
    if (agency !== 'Other') {
      return sanitizeAgencyName(agency);
    }

    const trimmedCustomAgency = sanitizeAgencyName(customAgency);
    if (!trimmedCustomAgency) return '';

    const normalizedCustomAgency = normalizeAgencyName(trimmedCustomAgency);
    return agencyLookup.get(normalizedCustomAgency) || trimmedCustomAgency;
  };

  const resolveAgencyForState = async (stateName, agencyName) => {
    const trimmedAgencyName = sanitizeAgencyName(agencyName);
    if (!stateName || !trimmedAgencyName) {
      return { agencyName: trimmedAgencyName, shouldUpsert: false };
    }

    const stateRef = doc(db, 'agenciesByState', stateName);
    const latestSnap = await getDoc(stateRef);
    const latestAgencies = latestSnap.exists() && Array.isArray(latestSnap.data().agencies)
      ? latestSnap.data().agencies
      : [];
    const latestLookup = buildAgencyLookup(latestAgencies).normalizedMap;
    const normalizedAgency = normalizeAgencyName(trimmedAgencyName);
    const existingAgency = latestLookup.get(normalizedAgency);

    if (existingAgency) {
      return { agencyName: existingAgency, shouldUpsert: false };
    }

    return { agencyName: trimmedAgencyName, shouldUpsert: true };
  };

  const upsertProfileSearchLookupsByState = async (stateName, agencyName, rankName, assignmentName) => {
    const trimmedStateName = sanitizeAgencyName(stateName);
    const trimmedAgencyName = sanitizeAgencyName(agencyName);
    const trimmedRankName = String(rankName || '').trim();
    const trimmedAssignmentName = String(assignmentName || '').trim();
    if (!trimmedStateName) return;

    const writes = [];
    if (trimmedAgencyName) {
      writes.push(setDoc(
        doc(db, 'agenciesByState', trimmedStateName),
        { agencies: arrayUnion(trimmedAgencyName) },
        { merge: true }
      ));
    }

    const profileLookupPayload = { updatedAt: serverTimestamp() };
    if (trimmedRankName) {
      profileLookupPayload.ranks = arrayUnion(trimmedRankName);
    }
    if (trimmedAssignmentName) {
      profileLookupPayload.assignments = arrayUnion(trimmedAssignmentName);
    }

    if (profileLookupPayload.ranks || profileLookupPayload.assignments) {
      writes.push(setDoc(
        doc(db, 'profileSearchLookupsByState', trimmedStateName),
        profileLookupPayload,
        { merge: true }
      ));
    }

    await Promise.all(writes);
  };



  const handleRegister = async () => {
  if (!email || !phone || !password || !confirmPassword || !firstName || !middleInitial || !lastName || !agency || !department || !rank || !state) {
    toast.error('Please complete all fields.');
    return;
  }

  if (agency === 'Other' && !sanitizeAgencyName(customAgency)) {
    toast.error('Enter your agency name.');
    return;
  }

  const domain = String(email || '').trim().toLowerCase().split('@')[1];
  if (!domain) {
    toast.error('Please enter a valid email address.');
    return;
  }

  if (blocklist.includes(domain)) {
    toast.error('Please use work email');
    return;
  }

  if (password !== confirmPassword) {
    toast.error('Passwords do not match.');
    return;
  }

  try {
    setIsRegistering(true);
    await proceedWithRegistration();
  } finally {
    setIsRegistering(false);
  }
};




  const proceedWithRegistration = async (referrerId = null) => {
  let userCred;

  try {
    let agencyFinal = resolveAgencyName();
    if (!agencyFinal) {
      toast.error('Please select or enter your agency.');
      return;
    }

    if (agency === 'Other') {
      const agencyResolution = await resolveAgencyForState(state, agencyFinal);
      agencyFinal = agencyResolution.agencyName;
    }

    userCred = await createUserWithEmailAndPassword(auth, email, password);

    await new Promise((resolve) => {
      if (auth.currentUser) {
        resolve();
        return;
      }

      const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
        if (currentUser) {
          unsubscribe();
          resolve();
        }
      });
    });

    let nextUserNumber;

    try {
      nextUserNumber = await runTransaction(db, async (transaction) => {
        const counterRef = doc(db, 'meta', 'counters');
        const counterDoc = await transaction.get(counterRef);

        if (!counterDoc.exists()) {
          throw new Error("Counter document does not exist!");
        }

        const lastNumber = counterDoc.data().lastUserNumber || 99;
        const newNumber = lastNumber + 1;

        transaction.update(counterRef, { lastUserNumber: newNumber });

        return newNumber;
      });
      console.log("Assigned userNumber:", nextUserNumber);
    } catch (err) {
      console.error("Failed to get next user number:", err);

      if (userCred?.user) {
        try {
          await userCred.user.delete();
          console.log("Cleaned up auth user after counter failure.");
        } catch (deleteErr) {
          console.error("Failed to delete auth user after counter failure:", deleteErr);
        }
      }

      toast.error("Registration failed. Please try again.");
      return;
    }

    const assignmentFinal = (customAssignment || department || '').trim();
    const rankFinal = (customRank || rank || '').trim();

    const userData = {
     email,
      phoneNumber: phone || '',
      phoneVerified: false,
      isFederal: !!isFederal,
      firstName: firstName || '',
      middleInitial: middleInitial || '',
      lastName: lastName || '',
      lastNameLower: lastName.trim().toLowerCase(),
      firstInitialLastName: `${firstName.charAt(0)}.${lastName}` || '',
      agency: agencyFinal,
      notificationPrefs: {
        messaging: true,
        bolo: true,
      },
      assignment: assignmentFinal,
      rank: rankFinal,
      dutyStatus: 'off-duty',
      state: state || '',
      userNumber: nextUserNumber,
      joined: serverTimestamp(),
      // ID verification — starts unverified for all new accounts
      verified: false,
      verificationStatus: null,
      subscription: {
    status: 'trial',
    type: 'monthly',
    renewalDate: getNextRenewalDate(),
    autoRenewal: false,
      },
    };

    await setDoc(doc(db, 'users', userCred.user.uid), userData);

	try {
  await Promise.all([
    upsertConfigArray(db, 'assignments', assignmentFinal),
    upsertConfigArray(db, 'ranks', rankFinal),
  ]);
} catch (e) {
  console.warn('⚠️ Failed to upsert registrationOptions:', e);
  // Non-blocking: user account is created; config update can be retried later.
}
    try {
      await upsertProfileSearchLookupsByState(
        state,
        agencyFinal,
        rankFinal,
        assignmentFinal
      );
    } catch (e) {
      console.warn('Failed to update profile search lookups:', e);
    }

localStorage.setItem(
  'currentUserProfile',
  JSON.stringify({ ...userData, uid: userCred.user.uid, verificationStatus: null, verified: false })
);

// define actionCodeSettings *here* so you can use the local `email`
// Try multiple authorized origins in case one isn't whitelisted or user came from a different host
const candidateOrigins = [
  window.location.origin,                        // current host (e.g., https://www.blue-wire.org)
  'https://www.blue-wire.org',                   // custom domain (www)
  'https://blue-wire.org',                       // apex (if you add it)
  'https://blue-wire-e382b.web.app',             // Firebase Hosting default
].filter(Boolean);

// Optional localization
auth.languageCode = 'en';

let sent = false;
let lastErr = null;

try {
  // Make sure auth has the freshest state
  await userCred.user.reload();
} catch {}

try {
  await emailVerificationViaResend({});
  console.log(`📤 Verification email sent via Resend for: ${email}`);
  sent = true;
} catch (e) {
  lastErr = e;
}

if (!sent) {
  console.error('❌ Resend email failed:', lastErr);
  toast.error(
    `Could not send verification email: ${
      lastErr?.message || lastErr || 'Unknown error'
    }`
  );
} else {
  toast.success(
    'Registration successful. A verification link has been sent to your email. ' +
    'Please check your inbox and spam folder.'
  );
}

localStorage.setItem('pendingEmail', email);
navigate('/verify-email', { replace: true });

  } catch (err) {
    console.error('Registration error:', err);

    // 🔷 Clean up auth account if Firestore write failed after auth creation
    if (userCred?.user) {
      try {
        await userCred.user.delete();
        console.log("Cleaned up auth user after Firestore failure.");
      } catch (deleteErr) {
        console.error("Failed to delete auth user:", deleteErr);
      }
    }

    toast.error(err.message || 'Registration failed.');
  }
};

  const handleStartOver = async () => {
    try {
      if (isResettingRegistration) return;
      setIsResettingRegistration(true);
      if (auth.currentUser) {
        await cleanupIncompleteRegistration();
        try {
          await auth.signOut();
        } catch (_) {}
      }
      setShowPhoneModal(false);
      toast.info('Registration reset. You can start over with the same email and phone.');
    } catch (e) {
      console.error('cleanupIncompleteRegistration error:', e);
      toast.error(e?.message || 'Failed to reset registration.');
    } finally {
      setIsResettingRegistration(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex justify-center px-0 py-8 sm:px-4 sm:py-12">
      
      <div className="w-full max-w-5xl rounded-none bg-white p-0 shadow-none sm:rounded-2xl sm:p-7 sm:shadow-xl sm:shadow-blue-100/40">
        <RegistrationProgress currentStep="register" className="mb-15" />
        <div className="flex flex-col items-center mb-5 text-center">
  <img
    src="/bluewire-messaging-logo.png"
    onError={(event) => { event.currentTarget.src = '/logo512.png'; }}
    alt="BlueWire"
    className="mb-3 h-24 w-24 object-contain sm:h-28 sm:w-28"
  />
  <h1 className="-mt-1 text-3xl font-bold text-slate-500">Registration</h1>
</div>

	<div className="mb-6 overflow-hidden rounded-none border border-slate-100 bg-white shadow-lg shadow-slate-200/60 sm:rounded-xl">
  <InfoRow icon={<FontAwesomeIcon icon={faShieldHalved} className="text-xl" />}>
    <strong className="text-700">Law Enforcement professionals only.</strong>
  </InfoRow>
  <InfoRow icon={<FontAwesomeIcon icon={faLock} className="text-xl" />}>
    The information you provide during registration is used to create your
    account and verify your identity and law-enforcement eligibility. Trusted
    third-party service providers assist with the necessary verification
    processes, including credential analysis and facial comparison. Your
    information is not sold or otherwise shared with third parties for
    unrelated purposes.
  </InfoRow>
  <InfoRow icon={<FontAwesomeIcon icon={faUserShield} className="text-xl" />}>
    We verify your <strong>email, phone number, and identity</strong> during registration to keep the platform secure.
  </InfoRow>
  <InfoRow icon={<FontAwesomeIcon icon={faHeadset} className="text-xl" />}>
    If you are having issues registering, contact us at <strong>thinlineteam@gmail.com</strong>.
  </InfoRow>
</div>


	<div className="grid grid-cols-1 gap-4 rounded-none border border-slate-100 bg-white p-2 shadow-lg shadow-slate-200/60 sm:rounded-xl sm:p-5">
  <FieldShell icon={<FontAwesomeIcon icon={faLocationDot} className="text-xl" />}>
    <div className="space-y-2">
      <p className="text-sm font-semibold text-gray-900">Select State</p>
  <select
    value={state}
    onChange={handleStateChange}
    className={selectInputClass}
    required
  >
    <option value="">Select State</option>
    {STATES.map((fullName) => (
      <option key={fullName} value={fullName}>{fullName}</option>
    ))}
  </select>
</div>
  </FieldShell>

          <FieldShell icon={<FontAwesomeIcon icon={faEnvelope} className="text-xl" />}>
          <div className="space-y-2">
            <div className="space-y-1">
              <p className="text-sm font-semibold text-gray-900">Email</p>
              <p className="text-sm text-gray-600">Enter your <b>work email address</b></p>
            </div>
            <input type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className={textInputClass} />
          </div>
          </FieldShell>

          <FieldShell icon={<FontAwesomeIcon icon={faPhone} className="text-xl" />}>
          <div className="space-y-2">
            <div className="space-y-1">
              <p className="text-sm font-semibold text-gray-900">Phone Number</p>
              <p className="text-sm text-gray-600">A verification code will be sent to this number</p>
            </div>
            <input
              type="tel"
              inputMode="numeric"
              autoComplete="tel"
              placeholder="Phone Number"
              value={phone}
              onChange={handlePhoneInputChange}
              className={textInputClass}
            />
          </div>
          </FieldShell>

          <FieldShell icon={<FontAwesomeIcon icon={faLock} className="text-xl" />}>
          <div className="space-y-2">
            <div className="space-y-1">
              <p className="text-sm font-semibold text-gray-900">Password</p>
              <p className="text-sm text-gray-600">At least 6 characters</p>
            </div>
            <input type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} className={textInputClass} />
            <input type="password" placeholder="Confirm Password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className={textInputClass} />
          </div>
          </FieldShell>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <FieldShell icon={<FontAwesomeIcon icon={faUser} className="text-xl" />}>
              <div className="space-y-2">
                <p className="text-sm font-semibold text-gray-900">First Name</p>
                <input type="text" placeholder="First Name" value={firstName} onChange={(e) => setFirstName(e.target.value)} className={textInputClass} />
              </div>
            </FieldShell>

            <FieldShell icon={<FontAwesomeIcon icon={faUser} className="text-xl" />}>
              <div className="space-y-2">
                <p className="text-sm font-semibold text-gray-900">Middle Initial</p>
                <input type="text" placeholder="Middle Initial" value={middleInitial} onChange={(e) => setMiddleInitial(e.target.value)} className={textInputClass} />
              </div>
            </FieldShell>
          </div>

          <FieldShell icon={<FontAwesomeIcon icon={faUser} className="text-xl" />}>
            <div className="space-y-2">
              <p className="text-sm font-semibold text-gray-900">Last Name</p>
              <input type="text" placeholder="Last Name" value={lastName} onChange={(e) => setLastName(e.target.value)} className={textInputClass} />
            </div>
          </FieldShell>
          <FieldShell icon={<FontAwesomeIcon icon={faBuilding} className="text-xl" />}>
          <div className="space-y-2">
            <div className="space-y-1">
              <p className="text-sm font-semibold text-gray-900">Agency</p>
              <p className="text-sm text-gray-600">Select your agency. If it’s not listed, choose “Other” to add it</p>
            </div>
            <div className="relative">
              <select
                value={agency}
                onChange={handleAgencyChange}
                className={`${selectInputClass} ${!state ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''}`}
                disabled={!state}
              >
                <option value="">{state ? 'Select Agency' : 'Select State First'}</option>
                {agencyOptions.map((a) => (
                  <option key={a} value={a}>{a}</option>
                ))}
                <option value="Other">Other</option>
              </select>
              {!state && (
                <button
                  type="button"
                  onClick={handleAgencyDropdownBlocked}
                  className="absolute inset-0"
                  aria-label="Select state before choosing agency"
                />
              )}
            </div>
          </div>

{state && agencyOptions.length === 0 && (
  <p className="text-sm text-gray-500 -mt-2">
    No agencies found for {state} yet. Select Other to add yours.
  </p>
)}

{agency === 'Other' && (
  <div className="space-y-2">
    <input
      type="text"
      placeholder="Enter Agency"
      value={customAgency}
      onChange={(e) => setCustomAgency(e.target.value)}
      className={textInputClass}
    />
    {customAgencySuggestions.length > 0 && (
      <div className="border border-gray-200 rounded-md bg-white shadow-sm max-h-48 overflow-y-auto">
        {customAgencySuggestions.map((agencyName) => (
          <button
            key={agencyName}
            type="button"
            onClick={() => {
              setAgency(agencyName);
              setCustomAgency('');
            }}
            className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100"
          >
            {agencyName}
          </button>
        ))}
      </div>
    )}
  </div>
)}
          </FieldShell>

          <FieldShell icon={<FontAwesomeIcon icon={faBriefcase} className="text-xl" />}>
          <div className="space-y-2">
            <div className="space-y-1">
              <p className="text-sm font-semibold text-gray-900">Assignment</p>
              <p className="text-sm text-gray-600">Select your assignment. If not listed, choose “Other” to add it</p>
            </div>
            <select
              value={department}
              onChange={(e) => setDepartment(e.target.value)}
              className={selectInputClass}
            >
              <option value="">Select Assignment</option>
              {assignmentOptions.map((a) => (
                <option key={a} value={a}>{a}</option>
              ))}
              <option value="Other">Other</option>
            </select>

            {department === 'Other' && (
              <input
                type="text"
                placeholder="Enter Assignment"
                value={customAssignment}
                onChange={(e) => setCustomAssignment(e.target.value)}
                className={textInputClass}
              />
            )}
          </div>
          </FieldShell>

          <FieldShell icon={<FontAwesomeIcon icon={faLayerGroup} className="text-xl" />}>
          <div className="space-y-2">
            <div className="space-y-1">
              <p className="text-sm font-semibold text-gray-900">Rank</p>
              <p className="text-sm text-gray-600">Select your rank. If not listed, choose “Other” to add it</p>
            </div>
            <select
              value={rank}
              onChange={(e) => setRank(e.target.value)}
              className={selectInputClass}
            >
              <option value="">Select Rank</option>
              {rankOptions.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
              <option value="Other">Other</option>
            </select>

            {rank === 'Other' && (
              <input
                type="text"
                placeholder="Enter Rank"
                value={customRank}
                onChange={(e) => setCustomRank(e.target.value)}
                className={textInputClass}
              />
            )}
          </div>
          </FieldShell>
          <label className="flex min-h-[44px] items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={isFederal}
              onChange={(e) => setIsFederal(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span>Federal employee</span>
          </label>
         


         <button
  onClick={handleRegister}
  disabled={isRegistering}
  className={`mt-2 flex min-h-[52px] items-center justify-center rounded-lg bg-blue-600 px-4 py-3 text-base font-semibold text-white shadow-md shadow-blue-200 ${
    isRegistering ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-700'
  }`}
>
  {isRegistering ? (
    <span className="flex items-center justify-center">
      <svg
        className="animate-spin h-5 w-5 mr-2 text-white"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        ></circle>
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
        ></path>
      </svg>
      Registering…
    </span>
  ) : (
    <>
      <span>Register</span>
      <FontAwesomeIcon icon={faChevronRight} className="ml-2" />
    </>
  )}
</button>
          <button onClick={() => navigate('/login')} className="min-h-[44px] text-blue-600 underline text-sm">
            Already have an account? Log in
          </button>
        </div>
      </div>
	
  <div
    className={`fixed inset-0 bg-black/50 flex items-center justify-center z-50 transition-opacity ${
      showPhoneModal ? 'opacity-100' : 'opacity-0 pointer-events-none'
    }`}
    aria-hidden={!showPhoneModal}
  >
    <PhoneVerify
      phone={phone}
      isOpen={showPhoneModal}
      onVerified={async () => {
        setShowPhoneModal(false);
        toast.success('Phone verified. Please click the verification link in your email.');
        navigate('/verify-email');
      }}
      onCancel={() => setShowPhoneModal(false)}
    />
    <div className="mt-4 text-center">
      <p className="text-sm text-gray-200 mb-2">
        Having trouble verifying?
      </p>
      <button
        onClick={handleStartOver}
        disabled={isResettingRegistration}
        className="px-3 py-2 rounded bg-red-600 text-white text-sm disabled:opacity-50"
      >
        {isResettingRegistration ? 'Clearing…' : 'Start over'}
      </button>
    </div>
  </div>
  <ToastContainer />
    </div>
  );
}
